import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Checkbox, FormControlLabel, MenuItem, Stack, TextField, Typography } from '@mui/material';
import IntentService, { errorMessage } from 'src/services/IntentService';
import { rubles, validBudget, safeExternalUrl } from 'src/utils/intentEconomics';

import { publicPage } from './WorkspaceHelp';

import type { Capabilities, Catalog, Order, Quote, Selection, Workspace } from 'src/services/IntentService';

export function requestKey() {
    if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 15) | 64;
    bytes[8] = (bytes[8] & 63) | 128;
    const text = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return (
        text.slice(0, 8) +
        '-' +
        text.slice(8, 12) +
        '-' +
        text.slice(12, 16) +
        '-' +
        text.slice(16, 20) +
        '-' +
        text.slice(20)
    );
}

export default function PackageCheckout({
    workspace,
    catalog,
    capabilities,
    wallet,
    dirty,
    onQuote,
    onOrder,
}: {
    workspace: Workspace;
    catalog: Catalog | null;
    capabilities: Capabilities | null;
    wallet: string | null;
    dirty: boolean;
    onQuote: (q: Quote | null) => void;
    onOrder: (o: Order) => void;
}) {
    const category = catalog?.categories.find((c) => c.id === workspace.profile.category_id);
    const [selection, setSelection] = useState<Selection>(workspace.profile.selection || { package_size: 30 });
    const [custom, setCustom] = useState(Boolean(selection.budget));
    const [amount, setAmount] = useState(selection.budget || '1500');
    const [quote, setQuote] = useState<Quote | null>(null);
    const [all, setAll] = useState(false);
    const [auto, setAuto] = useState(false);
    const [threshold, setThreshold] = useState('1');
    const [limit, setLimit] = useState('3000');
    const [useWallet, setUseWallet] = useState(false);
    const [agreed, setAgreed] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const key = useRef(requestKey());
    const categoryId = workspace.profile.category_id;
    useEffect(() => {
        setQuote(null);
        onQuote(null);
        setAgreed(false);
        key.current = requestKey();
    }, [workspace.id, workspace.revision, categoryId, dirty]); // A stale quote is never payable.
    const choose = (next: Selection) => {
        setSelection(next);
        setQuote(null);
        onQuote(null);
        setAgreed(false);
        key.current = requestKey();
    };
    const price = quote
        ? Math.max(0, Number(quote.budget) - (useWallet ? Math.min(Number(wallet), Number(quote.budget)) : 0))
        : 0;
    const calculate = async () => {
        setError('');
        setBusy(true);
        try {
            const q = await IntentService.quote(workspace.id, {
                ...selection,
                package_size: custom ? null : selection.package_size,
                budget: custom ? amount : null,
            });
            setQuote(q);
            onQuote(q);
            setThreshold(String(Math.min(q.leads - 1, Math.max(1, Math.round(q.leads / 10)))));
            setLimit(String(Number(q.budget) * 2));
            key.current = requestKey();
        } catch (e) {
            setError(errorMessage(e));
        } finally {
            setBusy(false);
        }
    };
    const pay = async () => {
        if (!quote) return;
        if (Date.parse(quote.expires_at) <= Date.now()) {
            setError('Расчёт устарел. Обновите его перед оплатой.');
            setQuote(null);
            onQuote(null);
            return;
        }
        setError('');
        setBusy(true);
        try {
            const order = await IntentService.checkout({
                quote_id: quote.id,
                request_key: key.current,
                use_wallet: useWallet,
                expected_card_amount: price.toFixed(2),
                auto: auto ? { threshold: Number(threshold), monthly_limit: limit } : null,
            });
            onOrder(order);
            setSubmitted(true);
            const url = safeExternalUrl(order.confirmation_url);
            if (order.state === 'pending' && url?.startsWith('https://')) window.location.assign(url);
        } catch (e) {
            setError(errorMessage(e));
        } finally {
            setBusy(false);
        }
    };
    return (
        <Box className="intent-checkout">
            <Typography
                component="h2"
                variant="h5"
            >
                Выберите объём
            </Typography>
            <Typography
                mt={1}
                mb={3}
                color="text.secondary"
            >
                {category?.name || 'Сначала выберите категорию в профиле потока'}
            </Typography>
            {dirty && (
                <Alert
                    severity="info"
                    sx={{ mb: 2 }}
                >
                    Сохраните изменения профиля и экономики перед расчётом.
                </Alert>
            )}
            <Box className="intent-package-grid">
                {(all ? category?.packages : category?.packages.slice(0, 3))?.map((p) => (
                    <Button
                        key={p.size}
                        className={'intent-package ' + (!custom && selection.package_size === p.size ? 'selected' : '')}
                        aria-pressed={!custom && selection.package_size === p.size}
                        onClick={() => {
                            setCustom(false);
                            choose({ ...selection, package_size: p.size, budget: null });
                        }}
                    >
                        <strong>{rubles(p.total)}</strong>
                        <span>{p.size} лидов</span>
                        <small>{rubles(p.price_per_lead)} за лид</small>
                    </Button>
                ))}
                <Button
                    className={'intent-package ' + (custom ? 'selected' : '')}
                    aria-pressed={custom}
                    onClick={() => {
                        setCustom(true);
                        choose({ ...selection, budget: amount, package_size: null });
                    }}
                >
                    <strong>Своя сумма</strong>
                    <span>от 1 500 ₽</span>
                </Button>
            </Box>
            {(category?.packages.length || 0) > 3 && (
                <Button
                    variant="text"
                    size="small"
                    onClick={() => setAll(!all)}
                >
                    {all ? 'Скрыть объёмы' : 'Все объёмы'}
                </Button>
            )}
            {custom && (
                <TextField
                    fullWidth
                    label="Общий бюджет, ₽"
                    type="number"
                    value={amount}
                    sx={{ mt: 2 }}
                    inputProps={{ min: 1500, max: 1000000, step: 0.01, inputMode: 'decimal' }}
                    onChange={(e) => {
                        setAmount(e.target.value);
                        choose({ ...selection, budget: e.target.value, package_size: null });
                    }}
                    error={!validBudget(amount)}
                    helperText={
                        validBudget(amount)
                            ? 'Включает выбранную обработку; остаток сохранится в рублях'
                            : 'От 1 500 ₽, до двух знаков после запятой'
                    }
                />
            )}
            <TextField
                select
                fullWidth
                label="Обработка"
                SelectProps={{ displayEmpty: true }}
                InputLabelProps={{ shrink: true }}
                value={selection.agent_package_id || ''}
                sx={{ mt: 3 }}
                onChange={(e) =>
                    choose({ ...selection, agent_package_id: e.target.value ? Number(e.target.value) : null })
                }
            >
                <MenuItem value="">Обрабатываю сам</MenuItem>
                {catalog?.processing.map((p) => (
                    <MenuItem
                        key={p.id}
                        value={p.id}
                    >
                        ИИ · {p.dialogs} диалогов — {rubles(p.total)}
                    </MenuItem>
                ))}
            </TextField>
            {selection.agent_package_id && (
                <Alert
                    severity="info"
                    sx={{ mt: 2 }}
                >
                    Покупка диалогов не подключает канал автоматически. Проверьте ИИ-менеджера во вкладке «Обработка».
                    Лиды и ИИ-диалоги учитываются отдельно.
                </Alert>
            )}
            {!quote && (
                <Button
                    variant="contained"
                    sx={{ mt: 3 }}
                    disabled={busy || dirty || !category?.available || (custom && !validBudget(amount))}
                    onClick={calculate}
                >
                    {busy ? 'Считаем…' : 'Рассчитать точный состав'}
                </Button>
            )}
            {quote && (
                <Box className="intent-order-summary">
                    <Typography
                        component="h3"
                        variant="h6"
                    >
                        Ваш заказ
                    </Typography>
                    <dl>
                        <div>
                            <dt>{quote.leads} лидов</dt>
                            <dd>{rubles(quote.lead_cost)}</dd>
                        </div>
                        <div>
                            <dt>{quote.dialogs ? quote.dialogs + ' ИИ-диалогов' : 'Обработка самостоятельно'}</dt>
                            <dd>{rubles(quote.processing_cost)}</dd>
                        </div>
                        <div>
                            <dt>Останется на счёте</dt>
                            <dd>{rubles(quote.remainder)}</dd>
                        </div>
                    </dl>
                    <Typography
                        color="text.secondary"
                        variant="body2"
                    >
                        За 30 дней в категории: {quote.demand.category_requests_30d} запросов. Это не прогноз по вашему
                        региону и правилам.
                    </Typography>
                    {quote.demand.category_requests_30d === 0 && (
                        <Alert
                            severity="warning"
                            sx={{ mt: 2 }}
                        >
                            Свежий спрос не подтверждён. Сохраните настройку или проверьте другую категорию до покупки.
                        </Alert>
                    )}
                    {Number(wallet) > 0 && (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={useWallet}
                                    onChange={(e) => setUseWallet(e.target.checked)}
                                />
                            }
                            label={'Использовать деньги со счёта: ' + rubles(wallet)}
                        />
                    )}
                    <Box mt={2}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={auto}
                                    disabled={!capabilities?.auto_topup}
                                    onChange={(e) => setAuto(e.target.checked)}
                                />
                            }
                            label="Включить автопополнение"
                        />
                        {!capabilities?.auto_topup && (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                Станет доступно после подключения повторных платежей.
                            </Typography>
                        )}
                        {auto && (
                            <Stack
                                spacing={2}
                                mt={1}
                            >
                                <TextField
                                    label="Пополнять, когда останется лидов"
                                    type="number"
                                    value={threshold}
                                    inputProps={{ min: 0, max: quote.leads - 1, step: 1 }}
                                    onChange={(e) => setThreshold(e.target.value)}
                                />
                                <TextField
                                    label="Лимит автоплатежей в месяц, ₽"
                                    type="number"
                                    value={limit}
                                    inputProps={{ min: Number(quote.budget), step: 0.01 }}
                                    onChange={(e) => setLimit(e.target.value)}
                                />
                                <Typography variant="body2">
                                    При остатке {threshold} лидов — тот же состав за {rubles(quote.budget)}. Не более{' '}
                                    {rubles(limit)} за месяц на аккаунт. Отключение доступно в оплате потока.
                                </Typography>
                            </Stack>
                        )}
                    </Box>
                    <FormControlLabel
                        sx={{ mt: 2 }}
                        control={
                            <Checkbox
                                checked={agreed}
                                onChange={(e) => setAgreed(e.target.checked)}
                            />
                        }
                        label={
                            <Typography variant="body2">
                                Проверил состав и принимаю{' '}
                                <a
                                    href={publicPage('/offer/index.html')}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    условия покупки
                                </a>
                                {auto ? ' и выбранное правило автопополнения' : ''}
                            </Typography>
                        }
                    />
                    {!capabilities?.checkout && (
                        <Alert
                            severity="info"
                            sx={{ mb: 2 }}
                        >
                            Новая оплата пока не подключена. Текущие покупки доступны в разделе «Потоки».
                        </Alert>
                    )}
                    {submitted && (
                        <Alert
                            severity="info"
                            sx={{ my: 2 }}
                        >
                            Заказ создан. Проверьте его статус выше или в истории покупок.
                        </Alert>
                    )}
                    <Button
                        fullWidth
                        variant="contained"
                        disabled={
                            submitted ||
                            quote.demand.category_requests_30d === 0 ||
                            busy ||
                            dirty ||
                            !agreed ||
                            !capabilities?.checkout ||
                            (auto &&
                                (!/^\d+$/.test(threshold) ||
                                    Number(threshold) >= quote.leads ||
                                    !validBudget(limit) ||
                                    Number(limit) < Number(quote.budget)))
                        }
                        onClick={pay}
                    >
                        {busy ? 'Проверяем оплату…' : 'Оплатить ' + rubles(price) + ' и запустить поиск'}
                    </Button>
                </Box>
            )}
            {error && (
                <Alert
                    severity="error"
                    sx={{ mt: 2 }}
                >
                    {error}
                </Alert>
            )}
        </Box>
    );
}
